// FLAC codec analysis: a full decoder for one frame at a time.
//
// A FLAC frame holds one subframe per channel. Each subframe guesses every
// sample from the samples before it (the "predictor") and stores only the
// error of that guess (the "residual") with Rice codes. Stereo frames may store
// a side channel (left − right) in place of left or right. Here we undo all of
// that to get PCM back, and we keep the parts so the UI can show them.
import { parseFrameHeader, skipRice, type FrameHeader, type StreamInfo } from '../parsers/flac';
import type { ParsedFile } from '../parsers/types';
import type { BarsView, CodecAnalysis, CodecLane, CodecView, PredictionView, TextView } from './types';

// ---------------------------------------------------------------------------
// Bit reader. Positions are relative to the frame's own bytes.

class Bits {
  readonly endBit: number;
  constructor(readonly b: Uint8Array, public pos: number) {
    this.endBit = b.length * 8;
  }
  /** Reads n bits (n ≤ 53), MSB first. */
  read(n: number): number {
    if (n === 0) return 0;
    let p = this.pos;
    if (p + n > this.endBit) throw new RangeError('The data runs past the end of the frame.');
    let v = 0, need = n;
    const b = this.b;
    while (need > 0) {
      const avail = 8 - (p & 7), take = avail < need ? avail : need;
      v = v * (1 << take) + ((b[p >> 3] >> (avail - take)) & ((1 << take) - 1));
      p += take;
      need -= take;
    }
    this.pos = p;
    return v;
  }
  signed(n: number): number {
    if (n === 0) return 0;
    const v = this.read(n);
    return v >= 2 ** (n - 1) ? v - 2 ** n : v;
  }
  /** Counts 0 bits up to (and consumes) the next 1 bit. */
  unary(): number {
    const b = this.b, start = this.pos;
    let i = start >> 3;
    if (i >= b.length) throw new RangeError('A unary code runs past the end of the frame.');
    let cur = b[i] & (0xff >> (start & 7));
    while (cur === 0) {
      if (++i >= b.length) throw new RangeError('A unary code runs past the end of the frame.');
      cur = b[i];
    }
    const one = i * 8 + Math.clz32(cur) - 24;
    this.pos = one + 1;
    return one - start;
  }
  skip(n: number): void {
    if (this.pos + n > this.endBit) throw new RangeError('The data runs past the end of the frame.');
    this.pos += n;
  }
}

// ---------------------------------------------------------------------------
// Frame and subframe decoding.

export type SubframeKind = 'constant' | 'verbatim' | 'fixed' | 'lpc';

export interface Partition { start: number; end: number; rice: number; bits: number; escaped?: boolean }

export interface Subframe {
  kind: SubframeKind;
  /** Predictor order (FIXED / LPC), else 0. */
  order: number;
  /** Low zero bits that every sample has (not stored). */
  wasted: number;
  /** Bits per sample of this subframe, incl. the side channel's extra bit. */
  sampleBits: number;
  /** Frame-relative bit range. */
  startBit: number;
  endBit: number;
  // The rest is set only by a full decode.
  /** Samples of this subframe (wasted bits restored), before stereo decorrelation is undone. */
  samples?: Int32Array;
  predicted?: Int32Array;
  residual?: Int32Array;
  coefs?: number[];
  precision?: number;
  shift?: number;
  rice2?: boolean;
  partitionOrder?: number;
  partitions?: Partition[];
  /** The value of a CONSTANT subframe. */
  constant?: number;
}

export interface DecodedFrame {
  header: FrameHeader;
  subframes: Subframe[];
  /** Final PCM per channel (only with a full decode). */
  pcm?: Int32Array[];
}

const FIXED_COEFS = [[], [1], [2, -1], [3, -3, 1], [4, -6, 4, -1]];

function readSubframe(r: Bits, n: number, bps: number, full: boolean): Subframe {
  const startBit = r.pos;
  if (r.read(1)) throw new RangeError('A subframe starts with a 1 bit, but it must be 0.');
  const t = r.read(6);
  let wasted = 0;
  if (r.read(1)) wasted = r.unary() + 1;
  const sb = bps - wasted;
  if (sb < 1) throw new RangeError(`Wasted bits (${wasted}) leave no bits per sample.`);
  const sf: Subframe = { kind: 'constant', order: 0, wasted, sampleBits: bps, startBit, endBit: startBit };

  if (t === 0) {
    const v = r.signed(sb);
    sf.constant = v * 2 ** wasted;
    if (full) {
      sf.samples = new Int32Array(n).fill(sf.constant);
      sf.predicted = sf.samples.slice();
      sf.residual = new Int32Array(n);
    }
  } else if (t === 1) {
    sf.kind = 'verbatim';
    if (full) {
      const s = new Int32Array(n);
      for (let i = 0; i < n; i++) s[i] = r.signed(sb);
      sf.samples = s;
    } else r.skip(sb * n);
  } else if ((t >= 8 && t <= 12) || t >= 32) {
    const lpc = t >= 32;
    const order = lpc ? t - 31 : t - 8;
    if (order > n) throw new RangeError(`Predictor order ${order} is larger than the block size.`);
    sf.kind = lpc ? 'lpc' : 'fixed';
    sf.order = order;
    const s = full ? new Int32Array(n) : null;
    if (s) for (let i = 0; i < order; i++) s[i] = r.signed(sb);
    else r.skip(order * sb);
    let coefs: number[] = FIXED_COEFS[order] ?? [];
    let shift = 0;
    if (lpc) {
      const prec = r.read(4) + 1;
      if (prec === 16) throw new RangeError('Coefficient precision 1111 is not allowed.');
      shift = r.signed(5);
      if (shift < 0) throw new RangeError('A negative quantization shift is not allowed.');
      if (s) {
        coefs = [];
        for (let i = 0; i < order; i++) coefs.push(r.signed(prec));
      } else r.skip(order * prec);
      sf.precision = prec;
      sf.shift = shift;
    }
    // Residual.
    const method = r.read(2);
    if (method > 1) throw new RangeError(`Residual coding method ${method} is reserved.`);
    const po = r.read(4);
    const parts = 1 << po;
    if (n % parts !== 0 || (n >> po) < order) throw new RangeError(`Partition order ${po} does not fit a block of ${n} samples.`);
    const per = n >> po;
    const pBits = method ? 5 : 4, escape = method ? 31 : 15;
    const partitions: Partition[] | null = s ? [] : null;
    const b = r.b, len = b.length;
    let idx = order;
    for (let i = 0; i < parts; i++) {
      const cnt = i === 0 ? per - order : per;
      const p0 = r.pos;
      const k = r.read(pBits);
      if (k === escape) {
        const raw = r.read(5);
        if (s) for (let j = 0; j < cnt; j++) s[idx++] = r.signed(raw);
        else r.skip(raw * cnt);
        partitions?.push({ start: i === 0 ? order : i * per, end: (i + 1) * per, rice: raw, bits: r.pos - p0, escaped: true });
      } else if (!s) {
        r.pos = skipRice(b, r.pos, cnt, k);
      } else {
        // Rice code: unary quotient, a 1 bit, then k low bits. The result is
        // folded: 0, −1, 1, −2, 2 … map to 0, 1, 2, 3, 4 …
        const scale = 2 ** k;
        let pos = r.pos;
        for (let j = 0; j < cnt; j++) {
          let bi = pos >> 3;
          if (bi >= len) throw new RangeError('Rice codes run past the end of the frame.');
          let cur = b[bi] & (0xff >> (pos & 7));
          while (cur === 0) {
            if (++bi >= len) throw new RangeError('Rice codes run past the end of the frame.');
            cur = b[bi];
          }
          const one = bi * 8 + Math.clz32(cur) - 24;
          const q = one - pos;
          r.pos = one + 1;
          const u = q * scale + (k ? r.read(k) : 0);
          pos = r.pos;
          s[idx++] = u % 2 ? -(u + 1) / 2 : u / 2;
        }
        partitions!.push({ start: i === 0 ? order : i * per, end: (i + 1) * per, rice: k, bits: r.pos - p0 });
      }
    }
    if (s) {
      // Undo the prediction: sample = guess + residual.
      const pred = new Int32Array(n);
      const res = new Int32Array(n);
      for (let i = 0; i < order; i++) pred[i] = s[i];
      const inv = 2 ** -shift;
      for (let i = order; i < n; i++) {
        let sum = 0;
        for (let j = 0; j < order; j++) sum += coefs[j] * s[i - 1 - j];
        const g = Math.floor(sum * inv);
        res[i] = s[i];
        pred[i] = g;
        s[i] = g + s[i];
      }
      sf.samples = s;
      sf.predicted = pred;
      sf.residual = res;
      sf.coefs = coefs;
      sf.partitions = partitions!;
    }
    sf.rice2 = method === 1;
    sf.partitionOrder = po;
  } else {
    throw new RangeError(`Subframe type ${t.toString(2).padStart(6, '0')} is reserved.`);
  }

  if (full) {
    const s = sf.samples!;
    if (!sf.predicted) { sf.predicted = s.slice(); sf.residual = new Int32Array(n); }
    if (wasted && sf.kind !== 'constant') {
      const m = 2 ** wasted;
      const pr = sf.predicted!, rs = sf.residual!;
      for (let i = 0; i < n; i++) { s[i] *= m; pr[i] *= m; rs[i] *= m; }
    }
  }
  sf.endBit = r.pos;
  return sf;
}

/** Index of the side channel for a channel assignment, or -1. */
const sideIndex = (a: number) => (a === 8 || a === 10 ? 1 : a === 9 ? 0 : -1);

/** Minimal STREAMINFO for header parsing, from the parsed file's audio info. */
export function streamInfoFrom(pf: ParsedFile): StreamInfo {
  const a = pf.audio;
  return {
    minBlock: 0, maxBlock: 0, minFrame: 0, maxFrame: 0, sampleRate: a.sampleRate ?? 0, channels: a.channels ?? 0,
    bps: a.bitsPerSample ?? 0, totalSamples: a.totalSamples ?? 0, md5Set: false,
  };
}

/**
 * Decodes one FLAC frame (its bytes start with the sync code). With full=false,
 * it only walks the structure (fast). Throws when the frame is damaged.
 */
export function decodeFlacFrame(frame: Uint8Array, si: StreamInfo | null, full = true): DecodedFrame {
  const h = parseFrameHeader(frame, 0, frame.length, si);
  if (!h) throw new RangeError('The frame header is not valid (bad sync code or CRC-8).');
  if (!h.bps) throw new RangeError('The bit depth is unknown.');
  const r = new Bits(frame, h.headerLen * 8);
  const side = sideIndex(h.assignment);
  const subframes: Subframe[] = [];
  for (let ch = 0; ch < h.channels; ch++) subframes.push(readSubframe(r, h.blockSize, h.bps + (ch === side ? 1 : 0), full));
  const out: DecodedFrame = { header: h, subframes };
  if (full) {
    const n = h.blockSize;
    const pcm = subframes.map(s => s.samples!.slice());
    const a = h.assignment;
    if (a === 8) { const [l, sd] = pcm; for (let i = 0; i < n; i++) pcm[1][i] = l[i] - sd[i]; }
    else if (a === 9) { const [sd, rr] = pcm; for (let i = 0; i < n; i++) pcm[0][i] = sd[i] + rr[i]; }
    else if (a === 10) {
      const [m, sd] = [subframes[0].samples!, subframes[1].samples!];
      for (let i = 0; i < n; i++) {
        const mid = m[i] * 2 + (sd[i] & 1);
        pcm[0][i] = (mid + sd[i]) >> 1;
        pcm[1][i] = (mid - sd[i]) >> 1;
      }
    }
    out.pcm = pcm;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Lanes.

const PREDICTOR: CodecLane = {
  name: 'Predictor',
  help: 'FLAC guesses each sample from the samples before it and stores only the error of the guess. A good guess leaves small errors, and small numbers need few bits. The lane shows the most complex predictor in each frame.',
  values: [
    { value: 'Constant', help: 'Every sample in the block has the same value, often digital silence. One number codes the whole block.' },
    { value: 'Verbatim', help: 'The samples are stored raw. No predictor saved space, which happens with noise-like sound.' },
    { value: 'Fixed', help: 'A fixed predictor of order 0–4. It extends the last few samples as a line or a smooth curve, so no coefficients are stored.' },
    { value: 'LPC ≤ 8', help: 'Linear prediction: a weighted sum of up to 8 earlier samples. The encoder fits the weights to this block and stores them.' },
    { value: 'LPC 9–12', help: 'Linear prediction from 9 to 12 earlier samples. Higher orders follow more complex sound, but the weights cost more bits.' },
    { value: 'LPC > 12', help: 'Linear prediction from more than 12 earlier samples. Only high compression settings (or high sample rates) use this.' },
  ],
};

const STEREO: CodecLane = {
  name: 'Stereo',
  help: 'Left and right are often almost the same. Then the side channel (left − right) holds only small numbers, which need fewer bits. The encoder picks the cheapest option for each frame.',
  values: [
    { value: 'Independent', help: 'Left and right are coded separately, because they differ too much for a side channel to help.' },
    { value: 'Left–side', help: 'Left and side (left − right) are stored. The decoder gets right back as left − side.' },
    { value: 'Right–side', help: 'Side (left − right) and right are stored. The decoder gets left back as side + right.' },
    { value: 'Mid–side', help: 'Mid (the average of left and right) and side are stored. This often suits sound near the center.' },
  ],
};

function predictorRank(sf: Subframe): number {
  switch (sf.kind) {
    case 'constant': return 0;
    case 'verbatim': return 1;
    case 'fixed': return 2;
    default: return sf.order <= 8 ? 3 : sf.order <= 12 ? 4 : 5;
  }
}

function stereoValue(h: FrameHeader): number {
  if (h.channels === 1) return -1;
  if (h.assignment >= 8) return h.assignment - 7;
  return 0;
}

// ---------------------------------------------------------------------------
// Text helpers.

const num = (v: number) => v.toLocaleString('en-US');
const round1 = (v: number) => (v >= 10 ? String(Math.round(v)) : String(+v.toFixed(1)));
const pct = (v: number) => `${Math.round(v * 100)}%`;
/** m:ss, or m:ss.s for short files where whole seconds are too coarse. */
const mmss = (t: number, fine = false) =>
  `${Math.floor(t / 60)}:${fine ? (t % 60).toFixed(1).padStart(4, '0') : String(Math.floor(t % 60)).padStart(2, '0')}`;

function channelNames(h: FrameHeader): string[] {
  const a = h.assignment;
  if (a === 8) return ['Left', 'Side'];
  if (a === 9) return ['Side', 'Right'];
  if (a === 10) return ['Mid', 'Side'];
  if (h.channels === 1) return ['Mono'];
  if (h.channels === 2) return ['Left', 'Right'];
  return Array.from({ length: h.channels }, (_, i) => `Channel ${i + 1}`);
}

function fixedWords(order: number): string {
  return [
    'guesses 0 every time, so the residual is the signal itself',
    'guesses that each sample repeats the one before',
    'extends a straight line through the last 2 samples',
    'extends a smooth curve through the last 3 samples',
    'extends a smooth curve through the last 4 samples',
  ][order];
}

function subframeCaption(sf: Subframe, name: string, n: number, rawBps: number): string {
  const bitsEach = (sf.endBit - sf.startBit) / n;
  let c: string;
  if (sf.kind === 'constant') {
    c = sf.constant === 0
      ? `Constant: every sample is 0, so this channel is silent here. One number codes all ${num(n)} samples.`
      : `Constant: every sample is ${num(sf.constant ?? 0)}. One number codes all ${num(n)} samples.`;
  } else if (sf.kind === 'verbatim') {
    c = `Verbatim: no predictor saved space, so all ${num(n)} samples are stored raw at ${sf.sampleBits - sf.wasted} bits each. This happens with noise-like sound.`;
  } else {
    const res = sf.residual!;
    let sum = 0;
    for (let i = sf.order; i < n; i++) sum += Math.abs(res[i]);
    const avg = n > sf.order ? sum / (n - sf.order) : 0;
    const head = sf.kind === 'lpc'
      ? `LPC order ${sf.order}: each guess is a weighted sum of the last ${sf.order} samples, with weights fitted to this block.`
      : `Fixed order ${sf.order}: the predictor ${fixedWords(sf.order)}.`;
    const good = bitsEach < rawBps * 0.6;
    c = `${head} The prediction misses by ${good ? 'only ' : ''}±${num(Math.round(avg))} on average, so each sample needs about ${round1(bitsEach)} bits instead of ${rawBps}.`;
  }
  if (name === 'Side') c += ' This is the side signal (left − right), not a speaker channel, so it has 1 extra bit of range.';
  if (name === 'Mid') c += ' This is the mid signal, the average of left and right.';
  if (sf.wasted) c += ` The low ${sf.wasted} bit${sf.wasted === 1 ? ' of every sample was' : 's of every sample were'} 0, so the encoder did not store ${sf.wasted === 1 ? 'it' : 'them'}.`;
  return c;
}

function predictorName(sf: Subframe): string {
  switch (sf.kind) {
    case 'constant': return 'Constant';
    case 'verbatim': return 'Verbatim';
    case 'fixed': return `Fixed order ${sf.order}`;
    default: return `LPC order ${sf.order}`;
  }
}

function stereoText(h: FrameHeader): string {
  switch (h.assignment) {
    case 8: return 'This frame stores **left** and **side** (left − right). The decoder gets right back as left − side. The side channel needs 1 extra bit of range, but when both channels sound alike its values are small, so it costs fewer bits than right would.';
    case 9: return 'This frame stores **side** (left − right) and **right**. The decoder gets left back as side + right. When both channels sound alike, side holds small values, so it costs fewer bits than left would.';
    case 10: return 'This frame stores **mid** (the average of left and right) and **side** (left − right). The decoder rebuilds left and right from them. The encoder picks this when the sound sits near the center, because side is then small.';
    default:
      if (h.channels === 1) return 'This stream has one channel, so there is no stereo choice.';
      if (h.channels === 2) return 'This frame codes **left and right separately**. Here the channels differ enough that a side channel would not save bits.';
      return `This frame codes its ${h.channels} channels separately. FLAC only has side-channel coding for stereo.`;
  }
}

// ---------------------------------------------------------------------------
// The analysis.

/** Where the FLAC frames come from: plain FLAC frames, or Ogg packets. */
export interface FlacSource {
  codec: string;
  si: StreamInfo;
  /** Number of decodable units (FLAC frames). */
  units: number;
  /** Bytes of unit u, starting at the sync code. */
  unitBytes(u: number): Uint8Array;
  /** Time span of unit u in seconds. */
  unitTime(u: number): [number, number];
  /** Unit that holds ParsedFile.frames[i], or -1. */
  unitOf(i: number): number;
}

interface Quick { rank: number; stereo: number; maxOrder: number; lpc: boolean; kinds: string[] }

export function flacAnalysis(src: FlacSource): CodecAnalysis {
  const { si } = src;
  const quick: (Quick | null | undefined)[] = new Array(src.units);

  const quickOf = (u: number): Quick | null => {
    const hit = quick[u];
    if (hit !== undefined) return hit;
    let q: Quick | null = null;
    try {
      const f = decodeFlacFrame(src.unitBytes(u), si, false);
      let rank = -1, maxOrder = 0, lpc = false;
      for (const sf of f.subframes) {
        rank = Math.max(rank, predictorRank(sf));
        if (sf.kind === 'lpc') { lpc = true; maxOrder = Math.max(maxOrder, sf.order); }
      }
      q = { rank, stereo: stereoValue(f.header), maxOrder, lpc, kinds: f.subframes.map(predictorName) };
    } catch {
      q = null;
    }
    quick[u] = q;
    return q;
  };

  const decisions = (i: number): number[] => {
    const u = src.unitOf(i);
    const q = u < 0 ? null : quickOf(u);
    return q ? [q.rank, q.stereo] : [-1, -1];
  };

  const detail = (i: number): CodecView[] => {
    const u = src.unitOf(i);
    if (u < 0) throw new Error('This piece is not part of a whole FLAC frame.');
    const bytes = src.unitBytes(u);
    const f = decodeFlacFrame(bytes, si, true);
    const h = f.header, n = h.blockSize;
    const names = channelNames(h);
    const views: CodecView[] = [];
    f.subframes.forEach((sf, k) => {
      const pv: PredictionView = {
        kind: 'prediction',
        title: `Subframe ${k + 1} · ${names[k]} · ${predictorName(sf)}`,
        caption: subframeCaption(sf, names[k], n, h.bps),
        signal: sf.samples!,
        predicted: sf.predicted!,
        residual: sf.residual!,
        warmup: sf.kind === 'verbatim' ? n : sf.kind === 'constant' ? 1 : sf.order,
        bitsPerSample: sf.sampleBits,
        partitions: sf.partitions,
        bits: sf.endBit - sf.startBit,
      };
      views.push(pv);
    });
    const bars: BarsView = {
      kind: 'bars',
      title: 'Bits per sample',
      caption: `How many bits each subframe spends per sample, next to the ${h.bps} bits of raw PCM. A short bar means the predictor did well.`,
      unit: 'bits',
      items: [
        ...f.subframes.map((sf, k) => ({ label: names[k], value: +((sf.endBit - sf.startBit) / n).toFixed(2), note: predictorName(sf) })),
        { label: 'Raw PCM', value: h.bps, note: 'Uncompressed' },
      ],
    };
    views.push(bars);
    const raw = (n * h.channels * h.bps) / 8;
    const used = bytes.length;
    const perSample = (used * 8) / (n * h.channels);
    const saving = 1 - used / raw;
    const text: TextView = {
      kind: 'text',
      title: 'Stereo choice and saving',
      body: `${stereoText(h)}\n\nThis frame uses **${num(used)} bytes** for ${num(n)} samples × ${h.channels} channel${h.channels === 1 ? '' : 's'}. Raw ${h.bps}-bit PCM would need ${num(Math.round(raw))} bytes. That is about ${round1(perSample)} bits per sample instead of ${h.bps}, ${saving >= 0 ? `a saving of ${pct(saving)}` : `${pct(-saving)} more than raw, because the sound is too noisy to predict`}.`,
    };
    views.push(text);
    return views;
  };

  const summarize = (): { level: 'info' | 'warn'; text: string }[] => {
    const out: { level: 'info' | 'warn'; text: string }[] = [];
    const U = src.units;
    if (!U) return out;
    // Predictors and stereo modes: look at up to 600 frames, spread evenly.
    const step = Math.max(1, U / 600);
    const ranks = new Array(PREDICTOR.values.length).fill(0);
    const st = new Array(STEREO.values.length).fill(0);
    const orders = new Map<number, number>();
    let seen = 0, stereoSeen = 0, bad = 0;
    for (let x = 0; x < U; x += step) {
      const q = quickOf(Math.floor(x));
      if (!q) { bad++; continue; }
      seen++;
      if (q.rank >= 0) ranks[q.rank]++;
      if (q.stereo >= 0) { st[q.stereo]++; stereoSeen++; }
      if (q.lpc) orders.set(q.maxOrder, (orders.get(q.maxOrder) ?? 0) + 1);
    }
    const sampled = step > 1 ? ` (from ${num(seen)} frames spread over the file)` : '';
    if (seen) {
      const parts = ranks.map((c, k) => [c, k]).filter(([c]) => c > 0).sort((a, b) => b[0] - a[0])
        .map(([c, k]) => `${PREDICTOR.values[k].value} ${pct(c / seen)}`);
      let t = `Predictors per frame${sampled}: ${parts.join(', ')}.`;
      if (orders.size) {
        const top = [...orders.entries()].sort((a, b) => b[1] - a[1])[0][0];
        t += ` The most common LPC order is ${top}.`;
      }
      out.push({ level: 'info', text: t });
    }
    if (stereoSeen) {
      const parts = st.map((c, k) => [c, k]).filter(([c]) => c > 0).sort((a, b) => b[0] - a[0])
        .map(([c, k]) => `${STEREO.values[k].value.toLowerCase()} ${pct(c / stereoSeen)}`);
      out.push({ level: 'info', text: `Stereo coding per frame: ${parts.join(', ')}. Side channels save bits when left and right are alike.` });
    }
    if (bad) out.push({ level: 'warn', text: `${num(bad)} of the checked frames could not be decoded. They may be damaged.` });

    // Bits per sample over time, from frame sizes (cheap, so use every frame).
    const ch = si.channels || 1, bps = si.bps;
    let totalBits = 0, totalSamples = 0;
    const per: { t0: number; t1: number; bits: number; samples: number }[] = [];
    for (let u = 0; u < U; u++) {
      const [t0, t1] = src.unitTime(u);
      const len = src.unitBytes(u).length;
      const samples = Math.round((t1 - t0) * (si.sampleRate || 44100)) * ch;
      totalBits += len * 8;
      totalSamples += samples;
      per.push({ t0, t1, bits: len * 8, samples });
    }
    if (totalSamples && bps) {
      const avg = totalBits / totalSamples;
      out.push({ level: 'info', text: `On average FLAC stores ${round1(avg)} bits per sample, against ${bps} bits for raw PCM. The frames are ${pct(avg / bps)} of the raw size.` });
    }
    const dur = per.length ? per[per.length - 1].t1 - per[0].t0 : 0;
    if (per.length >= 4 && dur > 0 && bps) {
      // Windows of about 1/20 of the file, at least 1 second.
      const win = Math.max(1, dur / 20);
      const wins: { t0: number; t1: number; v: number }[] = [];
      let a = 0;
      while (a < per.length) {
        let b = a, bits = 0, samples = 0;
        const t0 = per[a].t0;
        while (b < per.length && (per[b].t1 - t0 <= win || b === a)) { bits += per[b].bits; samples += per[b].samples; b++; }
        // A short tail window (the last frame is often short) would be noise.
        if (samples && (per[b - 1].t1 - t0 >= win / 2 || !wins.length)) wins.push({ t0, t1: per[b - 1].t1, v: bits / samples });
        a = b;
      }
      if (wins.length >= 2) {
        let lo = wins[0], hi = wins[0];
        for (const w of wins) { if (w.v < lo.v) lo = w; if (w.v > hi.v) hi = w; }
        const fine = dur < 120;
        const span = (w: { t0: number; t1: number }) => `${mmss(w.t0, fine)}–${mmss(w.t1, fine)}`;
        out.push({ level: 'info', text: `The most compressible part is ${span(lo)} (${round1(lo.v)} bits per sample): quiet or simple sound is easy to predict. The least compressible part is ${span(hi)} (${round1(hi.v)} bits per sample): loud, bright or noisy sound leaves larger errors.` });
      }
    }
    return out;
  };

  return {
    codec: src.codec,
    lanes: [PREDICTOR, STEREO],
    decisions,
    slowDecisions: true,
    detail,
    summarize,
  };
}

export function analyze(pf: ParsedFile, bytes: Uint8Array): CodecAnalysis | null {
  if (pf.format !== 'flac' || !pf.frames.length) return null;
  const frames = pf.frames;
  return flacAnalysis({
    codec: 'FLAC',
    si: streamInfoFrom(pf),
    units: frames.length,
    unitBytes: u => bytes.subarray(frames[u].start, frames[u].end),
    unitTime: u => frames[u].time ?? [0, 0],
    unitOf: i => (i >= 0 && i < frames.length ? i : -1),
  });
}
